'use client';

import { makeAutoObservable } from 'mobx';
import {
  UpdateUserDocument,
  UpdateUserMutation,
  UpdateUserMutationVariables,
  DeletePaymentMethodDocument,
  DeletePaymentMethodMutation,
  DeletePaymentMethodMutationVariables,
  PaymentMethodsDocument,
  PaymentMethodsQuery,
  PaymentMethodsQueryVariables,
  SetDefaultPaymentMethodDocument,
  SetDefaultPaymentMethodMutation,
  SetDefaultPaymentMethodMutationVariables,
} from './mutations.generated';
import { Address } from './address';
import { PaymentMethod, type PaymentMethodJSON } from './payment-method';
import { apiClient } from '@instamenu/context-api';
import { debounce } from 'lodash-es';

export type UserJSON = Partial<
  NonNullable<NonNullable<UpdateUserMutation['updateUser']>['user']>
> & {
  id: string;
  paymentMethods?: PaymentMethodJSON[];
};
const byKey = new Map<string, User>();

export class User {
  json!: UserJSON;
  _deliveryAddresses: Address[] = [];
  _paymentMethods: PaymentMethod[] = [];

  private constructor(json: UserJSON) {
    this.updateFromJson(json);
    makeAutoObservable(this);
  }

  static fromJSON(json: UserJSON) {
    const key = json.id;
    let instance = byKey.get(key);
    if (!instance) {
      instance = new User(json);
      byKey.set(instance.id, instance);
    } else {
      instance.updateFromJson(json);
    }
    return instance;
  }

  updateFromJson(json: Partial<UserJSON>) {
    this.json = { ...this.json, ...json };
    if (json.deliveryAddresses) {
      this._deliveryAddresses = json.deliveryAddresses.map((a) => Address.fromJSON(a));
    }
    if (json.paymentMethods) {
      this._paymentMethods = json.paymentMethods.map((pm) => PaymentMethod.fromJSON(pm));
    }
  }

  get employers() {
    return this.json.employers ?? [];
  }

  get name() {
    return this.json.name;
  }
  get id() {
    return this.json.id;
  }
  get email() {
    return this.json.email;
  }
  get phone() {
    return this.json.phone;
  }
  get deliveryAddresses() {
    return this._deliveryAddresses;
  }
  get paymentMethods() {
    return this._paymentMethods;
  }
  get defaultDeliveryAddress() {
    return (
      this.deliveryAddresses.find((a) => a.id === this.defaultDeliveryAddressId) ??
      this.deliveryAddresses[0]
    );
  }
  get defaultDeliveryAddressId() {
    return this.json.defaultDeliveryAddressId;
  }
  get defaultPaymentMethod() {
    return this.json.defaultPaymentMethod;
  }
  get firstName() {
    return this.json.firstName;
  }
  get lastName() {
    return this.json.lastName;
  }

  addAddress(address: Address, save = true) {
    this.deliveryAddresses.push(address);
    this.json.defaultDeliveryAddressId = address.id;
    if (save)
      this.sync({
        deliveryAddressesAttributes: [{ ...address.asDeliveryAddressAttributes(), default: true }],
      });
  }

  discardAddress(address: Address) {
    this._deliveryAddresses = this.deliveryAddresses.filter((a) => a.key !== address.key);
    if (this.defaultDeliveryAddressId === address.id)
      this.json.defaultDeliveryAddressId = undefined;
    if (address.id) this.sync({ deliveryAddressesAttributes: [{ id: address.id, discard: true }] });
  }

  setPaymentMethods(paymentMethods: PaymentMethodJSON[]) {
    this._paymentMethods = paymentMethods.map((pm) => PaymentMethod.fromJSON(pm));
  }

  async refreshPaymentMethods() {
    const res = await apiClient()
      .query<PaymentMethodsQuery, PaymentMethodsQueryVariables>(PaymentMethodsDocument, {})
      .toPromise();
    if (res.data?.paymentMethods) this.setPaymentMethods(res.data.paymentMethods);
  }

  async deletePaymentMethod(id: string) {
    this._paymentMethods = this.paymentMethods.filter((pm) => pm.id !== id);
    await apiClient()
      .mutation<
        DeletePaymentMethodMutation,
        DeletePaymentMethodMutationVariables
      >(DeletePaymentMethodDocument, { input: { id } })
      .toPromise();
  }

  async setDefaultPaymentMethod(id: string) {
    this._paymentMethods = this.paymentMethods.map((pm) =>
      PaymentMethod.fromJSON({ ...pm.json, isDefault: pm.id === id }),
    );
    await apiClient()
      .mutation<
        SetDefaultPaymentMethodMutation,
        SetDefaultPaymentMethodMutationVariables
      >(SetDefaultPaymentMethodDocument, { input: { id } })
      .toPromise();
  }

  syncVars?: UpdateUserMutationVariables['input'];
  sync(vars: UpdateUserMutationVariables['input']) {
    this.syncVars = Object.assign({}, this.syncVars, vars);
    this.debouncedSync(++this.currentUpdateId);
  }

  private debouncedSync = debounce((updateId: number) => {
    if (!this.syncVars) return;
    apiClient()
      .mutation<UpdateUserMutation, UpdateUserMutationVariables>(UpdateUserDocument, {
        input: this.syncVars,
      })
      .toPromise()
      .then((res) => {
        this.syncVars = undefined;
        if (this.currentUpdateId !== updateId) return;
        this.currentUpdateId = 0;
        if (res.error) throw new Error(res.error.message);
        if (!res.data?.updateUser?.user) throw new Error('updateUser.user is undefined');
        this.updateFromJson(res.data.updateUser.user);
      });
  }, 500);

  private currentUpdateId = 0;
  update(input: UpdateUserMutationVariables['input']) {
    this.updateFromJson(input);
    this.sync(input);
  }

  static async init(vars: UpdateUserMutationVariables['input']) {
    const res = await apiClient()
      .mutation<UpdateUserMutation, UpdateUserMutationVariables>(UpdateUserDocument, {
        input: { ...vars, hasConfirmedName: true },
      })
      .toPromise();
    if (!res.data?.updateUser?.user) throw new Error('could not initialize the user');
    return User.fromJSON(res.data.updateUser.user);
  }

  get permissions() {
    if (!this.json.employments) return {};
    return this.json.employments?.reduce((acc: { [restaurantId: string]: string[] }, emp) => {
      acc[emp.restaurant.id] = emp.permissions ?? [];
      return acc;
    }, {});
  }
}
